/**
 * Iter 68 — Admin self-service credentials card.
 *
 * Two side-by-side actions for the logged-in admin:
 *   1. Change password — no current-password challenge (per product decision).
 *   2. Change email — sends a confirmation link to the new address; the swap
 *      only commits when the new-address recipient clicks the link.
 *
 * Replaces the generic <ChangePasswordCard /> on the admin "Account" tab,
 * because admins use the dedicated /api/admin/me/password endpoint which
 * skips the current-password requirement that customer/vendor self-service
 * still enforces.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, Mail, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function AdminCredentialsPanel() {
  const [me, setMe] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [savingPwd, setSavingPwd] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);

  const reload = async () => {
    try {
      const r = await api.get("/admin/me").then((res) => res.data);
      setMe(r);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => { reload(); }, []);

  const changePassword = async () => {
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }
    setSavingPwd(true);
    try {
      await api.post("/admin/me/password", { new_password: newPassword });
      toast.success("Password updated.");
      setNewPassword("");
      setConfirmPassword("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingPwd(false);
    }
  };

  const requestEmailChange = async () => {
    const target = newEmail.trim().toLowerCase();
    if (!target || !target.includes("@")) {
      toast.error("Enter a valid email address.");
      return;
    }
    setSavingEmail(true);
    try {
      const r = await api.post("/admin/me/email/request-change", { new_email: target }).then((res) => res.data);
      toast.success(`Confirmation email sent to ${r.sent_to}. Click the link to finalise the change.`);
      setNewEmail("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingEmail(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-credentials-panel">
      {me && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <ShieldCheck className="h-4 w-4 text-emerald-600" /> Account
            </CardTitle>
            <CardDescription>
              {me.email} · {me.is_super_admin ? "Super admin" : "Admin"}
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5" /> Change password</CardTitle>
          <CardDescription>Set a new password for your admin account. You'll stay signed in on this device.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 max-w-md">
          <div className="space-y-2">
            <Label htmlFor="admin-new-pwd">New password</Label>
            <Input
              id="admin-new-pwd"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              data-testid="admin-new-password"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="admin-confirm-pwd">Confirm new password</Label>
            <Input
              id="admin-confirm-pwd"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              data-testid="admin-confirm-password"
            />
          </div>
          <Button onClick={changePassword} disabled={savingPwd} data-testid="admin-save-password">
            {savingPwd ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Update password
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Mail className="h-5 w-5" /> Change email</CardTitle>
          <CardDescription>
            We send a confirmation link to the new address. The change only commits when
            you click the link — until then your current email stays active.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 max-w-md">
          <div className="space-y-2">
            <Label htmlFor="admin-new-email">New email address</Label>
            <Input
              id="admin-new-email"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid="admin-new-email"
            />
          </div>
          <Button onClick={requestEmailChange} disabled={savingEmail} data-testid="admin-request-email-change">
            {savingEmail ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Send confirmation link
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
